/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useRef } from "react";
import Parser from "rss-parser";
import { Button, Form, Segment, Accordion } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { authCheckCallApi } from "../../Authenticator/utils";
import { getS3PresignedUrl } from "../../../graphql/user";
import { useApolloClient } from "@apollo/react-hooks";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";

const Content: React.FC = () => {
  const noFileMessage = "No file selected";
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [currentRssFile, setCurrentRssFile] = useState<string>(noFileMessage);
  const [currentGuidelinesFile, setCurrentGuidelinesFile] =
    useState<string>(noFileMessage);
  const rssRef: any = useRef(null);
  const guideLinesRef: any = useRef(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [newsActive, setNewsActive] = useState<boolean>(true);
  const [guidelinesActive, setGuidelinesActive] = useState<boolean>(false);
  const client = useApolloClient();

  const resetFiles = () => {
    setCurrentRssFile(noFileMessage);
    setCurrentGuidelinesFile(noFileMessage);
    rssRef.current.value = "";
    guideLinesRef.current.value = "";
  };

  const getCurrentFileName = (ref: any) => {
    if (
      !ref ||
      !ref.current ||
      !ref.current.files ||
      ref.current.files.length !== 1
    ) {
      return null;
    }
    return ref.current.files[0].name;
  };

  const uploadFile = async (
    fileData: string,
    uploadUrl: string,
    mimeType: string
  ) => {
    await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": mimeType,
        "Cache-Control": "no-cache, max-age=0",
      },
      body: fileData,
    })
      .then((response) => {
        if (!response.ok) {
          setMessage({
            title: "Upload Failed",
            message: "Server reported an error.",
          });
        } else {
          setMessage({
            title: "Upload Succeeded",
            message: "File uploaded successfully.",
          });
        }
      })
      .catch((e) => {
        setMessage({
          title: "Upload Failed",
          message: e.message,
        });
      })
      .finally(() => {
        setLoading(false);
        resetFiles();
      });
  };

  const getPresignedUrl = (fileData: string, ref: any) => {
    const type = ref === rssRef ? "news" : "protection";
    const mimeType = ref === rssRef ? "application/xml" : "application/json";
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: getS3PresignedUrl,
          variables: { uploadType: type, mimeType: mimeType },
        })
        .then((result) => {
          if (!result.data || !result.data.getS3PresignedUrl) {
            setMessage({
              title: "Upload Failed",
              message: "Unable to retrieve upload URL.",
            });
            return;
          }
          uploadFile(
            fileData,
            result.data.getS3PresignedUrl.uploadUrl,
            mimeType
          );
        })
        .catch((e) => {
          setMessage({
            title: "Upload Failed",
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const checkFileFormat = (data: string, ref: any) => {
    const checkFormatFailed = (err: string) => {
      setMessage({
        title: "Upload Error",
        message: `Please check the format of the selected file. ${err}`,
      });
      setLoading(false);
    };

    if (ref === rssRef) {
      const parser = new Parser();
      parser.parseString(data, (err, _) => {
        if (err) {
          return checkFormatFailed(err.message);
        }
        getPresignedUrl(data, ref);
      });
    } else {
      try {
        const protectionJson = JSON.parse(data);
        if (
          !Array.isArray(protectionJson.android) ||
          !Array.isArray(protectionJson.ios) ||
          !Array.isArray(protectionJson.firstApp)
        ) {
          return checkFormatFailed("Wrong format for protection guidelines.");
        }
        getPresignedUrl(data, ref);
      } catch (e) {
        return checkFormatFailed(e);
      }
    }
  };

  const onUploadFile = (ref: any) => {
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (
          !evt.target ||
          !evt.target.result ||
          typeof evt.target.result !== "string"
        ) {
          setMessage({
            title: "Upload Error",
            message: "Failed to process selected file.",
          });
          setLoading(false);
          return;
        }

        const data = evt.target.result;
        checkFileFormat(data, ref);
      };
      reader.onerror = () => {
        setMessage({
          title: "Upload Error",
          message: "Please select a different file.",
        });
        setLoading(false);
        return;
      };
      reader.readAsText(ref.current.files[0], "UTF-8");
    } catch (e) {
      setMessage({
        title: "Upload Failed",
        message: e.message,
      });
      setLoading(false);
    }
  };

  return (
    <Segment basic>
      <Accordion>
        <Accordion.Title
          className={styles.advancedTitle}
          active={newsActive}
          index={0}
          onClick={() => {
            setNewsActive(!newsActive);
          }}
        >
          <Ico
            name={newsActive === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          News Feed
        </Accordion.Title>
        <Accordion.Content active={newsActive} style={{ padding: "1rem" }}>
          <Form
            onSubmit={() => onUploadFile(rssRef)}
            style={{ marginLeft: "2rem" }}
          >
            <Form.Group inline className={styles.landingFormGroup}>
              <label htmlFor="rssUpload" className={styles.uploadButton}>
                Select RSS XML file
              </label>
              <input
                id="rssUpload"
                type="file"
                ref={rssRef}
                onChange={() => {
                  setCurrentRssFile(
                    `Selected file: ${getCurrentFileName(rssRef)}`
                  );
                }}
              />

              <Button
                secondary
                className={cStyles.buttonSecond}
                disabled={!getCurrentFileName(rssRef)}
                loading={loading}
                type="submit"
              >
                <Ico name="upload" style={{ marginRight: "10px" }} />
                Upload news
              </Button>
            </Form.Group>
            <div
              className={cStyles.input}
              style={{ padding: "1rem", marginBottom: "2rem" }}
            >
              {currentRssFile}
            </div>

            <div>
              <a
                href="/news/news.xml"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.appConfigLink}
              >
                Show current news feed
              </a>
            </div>
          </Form>
        </Accordion.Content>

        <Accordion.Title
          className={styles.advancedTitle}
          active={guidelinesActive}
          index={1}
          onClick={() => {
            setGuidelinesActive(!guidelinesActive);
          }}
        >
          <Ico
            name={guidelinesActive ? "down" : "right"}
            className={styles.accordionIcon}
          />
          Protection Guidelines
        </Accordion.Title>
        <Accordion.Content
          active={guidelinesActive}
          style={{ padding: "1rem" }}
        >
          <Form
            onSubmit={() => onUploadFile(guideLinesRef)}
            style={{ marginLeft: "2rem" }}
          >
            <Form.Group inline className={styles.landingFormGroup}>
              <label htmlFor="guideLinesUpload" className={styles.uploadButton}>
                Select JSON file
              </label>

              <input
                id="guideLinesUpload"
                type="file"
                ref={guideLinesRef}
                onChange={() => {
                  setCurrentGuidelinesFile(
                    `Selected file: ${getCurrentFileName(guideLinesRef)}`
                  );
                }}
              />

              <Button
                secondary
                className={cStyles.buttonSecond}
                type="submit"
                disabled={!getCurrentFileName(guideLinesRef)}
                loading={loading}
              >
                <Ico name="upload" style={{ marginRight: "10px" }} />
                Upload Guidelines
              </Button>
            </Form.Group>

            <div
              className={cStyles.input}
              style={{ padding: "1rem", marginBottom: "2rem" }}
            >
              {currentGuidelinesFile}
            </div>

            <div>
              <a
                href="/protection/protection.json"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.appConfigLink}
              >
                Show current guidelines
              </a>
            </div>
          </Form>
        </Accordion.Content>
      </Accordion>

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </Segment>
  );
};

export default Content;
